import React, { useState, useEffect } from 'react';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiPackage } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ConfirmationModal from '../components/confirmation';
import RestockModal from '../components/RestockModal';
import './viewInventory.css';


const PriceDisplay = ({ price, unit }) => {
  const unitMultiplier = {
    set: 6,
    dozen: 12,
    piece: 1,
    kg: 1
  };
  
  const numericPrice = Number(price) || 0;
  const showConversion = ['set', 'dozen'].includes(unit);

  return (
    <div className="price-display">
      <span className="unit-price">
        Rs.{(numericPrice * unitMultiplier[unit]).toFixed(2)}
        {unit !== 'piece' && unit !== 'kg' && `/${unit}`}
      </span>
      {showConversion && (
        <span className="piece-price">
          (Rs.{numericPrice.toFixed(2)}/pc)
        </span>
      )}
    </div>
  );
};

const InventoryTable = ({ searchTerm, onDelete, onRestock, refreshTrigger }) => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 1
  });

  

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/items', {
          params: {
            page: pagination.currentPage,
            limit: pagination.itemsPerPage,
            search: searchTerm
          }
        });

        setPagination(prev => ({
          ...prev,
          totalItems: response.data.pagination.total_items,
          totalPages: response.data.pagination.total_pages
        }));

        setInventory(response.data.data);
      } catch (err) {
        console.error('Error fetching inventory:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [searchTerm, pagination.currentPage, pagination.itemsPerPage, refreshTrigger]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };

  if (loading) {
    return <div className="loading">Loading inventory...</div>;
  }

  return (
    <>
      <table className="inventory-table">
        <thead>
         <tr>
            <th>Product</th>
            <th>Cost Price</th>
            <th>Selling Price</th>
            <th>Stock</th>
            <th>Supplier</th>
            <th>Code</th>
            <th>Updated</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {inventory.map((item) => (
            <tr key={item.item_id}>
             <td>
              <div className="product-name-container">
                <span className="product-name-text">{item.item_name}</span>
                {item.item_code && (
                  <span className="product-code-tooltip">{item.item_code}</span>
                )}
              </div>
            </td>
              
              <td className="price-cell">
                <PriceDisplay price={item.cost_price} unit={item.unit} />
              </td>
              
              <td className="price-cell">
                <PriceDisplay price={item.selling_price} unit={item.unit} />
              </td>
              
              <td>
                <div className="stock-display">
                  <span className={`quantity-badge ${
                    item.quantity === 0 ? 'quantity-out' :
                    item.quantity < 10 ? 'quantity-low' : 'quantity-good'
                  }`}>
                    {item.quantity} {item.unit === 'kg' ? 'kg' : 'pcs'}
                  </span>
                  {item.unit !== 'piece' && item.unit !== 'kg' && (
                    <span className="converted-quantity">
                      {Math.floor(Number(item.quantity) / 
                       (item.unit === 'set' ? 6 : 12))} {item.unit}
                    </span>
                  )}
                </div>
              </td>
              
              <td>{item.supplier_name || 'N/A'}</td>

            <td>{item.item_code || 'No Code'}</td>
              
              <td>
                {item.updated_at ? 
                  new Date(item.updated_at).toLocaleDateString('en-IN') : 'N/A'}
              </td>

              
              <td>
                <div className="action-buttons">
                  <button
                    className="restock-btn"
                    onClick={() => onRestock(item)}
                    title="Add stock"
                  >
                    <FiPackage />
                  </button>
                  <button
                    className="edit-btn"
                    onClick={() => navigate(`/inventory/edit/${item.item_id}`)}
                  >
                    <FiEdit2 />
                  </button>
                <button
  className="delete-btn"
  onClick={() => onDelete(item.item_id, item.item_name)}
>
  <FiTrash2 />
</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {pagination.totalPages > 1 && (
        <div className="pagination-controls">
          <button
            onClick={() => handlePageChange(1)}
            disabled={pagination.currentPage === 1}
          >
            First
          </button>
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
          >
            Previous
          </button>

          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
            const pageNum = pagination.currentPage <= 3
              ? i + 1
              : Math.min(pagination.totalPages - 4, pagination.currentPage - 2) + i;
            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={pagination.currentPage === pageNum ? 'active' : ''}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages}
          >
            Next
          </button>
          <button
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={pagination.currentPage === pagination.totalPages}
          >
            Last
          </button>
        </div>
      )}
    </>
  );
};

const ViewInventory = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  const [restockItem, setRestockItem] = useState(null); // item currently being restocked, or null

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const res = await axios.get('/suppliers');
        setSuppliers(res.data);
      } catch (err) {
        console.error('Error fetching suppliers:', err);
      }
    };
    fetchSuppliers();
  }, []);

  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemNameToDelete, setItemNameToDelete] = useState('');
  const [deleteError, setDeleteError] = useState(null);

  const handleDelete = (id, name) => {
    setItemToDelete(id);
    setItemNameToDelete(name);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/items/${itemToDelete}`);
      setRefreshTrigger(prev => prev + 1);
      setShowDeleteModal(false);
    } catch (err) {
      console.error('Error deleting item:', err);
      setShowDeleteModal(false);
      setDeleteError(err.response?.data?.error || 'Failed to delete item.');
    }
  };

  const handleRestockSuccess = () => {
    setRestockItem(null);
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <div className="inventory-container">
      {deleteError && (
        <div className="api-notification">
          {deleteError}
          <button className="close-btn" onClick={() => setDeleteError(null)}>×</button>
        </div>
      )}

      <div className="inventory-header">
        <h1>Inventory</h1>
        <div className="inventory-controls">
          <div className="search-input-container">
            <FiSearch />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button onClick={() => navigate('/inventory/add')}
            className="add-product-btn">

            <FiPlus /> Add Product
          </button>
        </div>
      </div>

      <InventoryTable 
        searchTerm={searchTerm} 
        onDelete={handleDelete} 
        onRestock={setRestockItem}
        refreshTrigger={refreshTrigger} 
      />

      <ConfirmationModal
  show={showDeleteModal}
  title="Delete Item"
  message={`Are you sure you want to delete "${itemNameToDelete}"? This action cannot be undone.`}
  onConfirm={handleConfirmDelete}
  onCancel={() => setShowDeleteModal(false)}
/>

      {restockItem && (
        <RestockModal
          item={restockItem}
          suppliers={suppliers}
          onClose={() => setRestockItem(null)}
          onSuccess={handleRestockSuccess}
        />
      )}
    </div>
  );
};

export default ViewInventory;